import React, { useState } from 'react'
import { Alert, Button, Input, Label, Form, FormGroup, FormText, Spinner } from 'reactstrap'
import { Currency } from '../components'
import { useFormik } from 'formik'
import { useIntl } from 'react-intl'
import { makeSelectCurOrg } from 'logic/orgs'
import { useSelector } from 'react-redux'
import * as Yup from 'yup'
import '../assets/scss/simpl.scss'
import messages from '../i18n/messages'

const defaultText = `
Sie erhalten eine E-Mail an die Adresse von der aus die Tickets ursprünglich bezogen wurden. Dort finden Sie ebenfalls diese Daten.
<br />Der Gutschein ist Online einlösbar. Wählen Sie den gewünschten Termin. Gehen Sie auf "bezahlen" und lösen Sie den Gutscheincode am
Ende bei der Auswahl der Bezahlform ein.
`

function ConversionSuccessful ({ voucherInfo, text }) {
  return (
    <div className="text-center">
      <Alert color="success">
        Ihre Tickets wurden erfolgreich in einen Gutschein umgewandelt!
      </Alert>
      <div className="d-flex justify-content-center">
        <table className="table" style={{ width: '400px' }}>
          <tbody>
            <tr>
              <td className="text-right"><Label>Gutschein-Code:</Label></td>
              <td className="text-left"><strong>{voucherInfo.code}</strong></td>
            </tr>
            <tr>
              <td className="text-right"><Label>Wert:</Label></td>
              <td className="text-left"><strong><Currency value={voucherInfo.value} /></strong></td>
            </tr>
          </tbody>
        </table>
      </div>
      <div className="text-muted text-small">
        <div dangerouslySetInnerHTML={{ __html: text || defaultText}} />
      </div>
    </div>
  )
}

function ConversionForm ({ error, loading, formik }) {
  const { formatMessage: f } = useIntl()
  return(
    <Form className="simpl-form" onSubmit={formik.handleSubmit}>
      <h3 className="conv-header">{`${f(messages.convertTicketToVoucher)}`}</h3>
      <FormGroup>
        <Label for="invoice_num">Rechnungsnummer</Label>
        {formik.errors.invoice_num && formik.touched.invoice_num
          && <FormText color="danger">{formik.errors.invoice_num}</FormText> }
        <Input
          type="text"
          id="invoice_num"
          name="invoice_num"
          disabled={loading}
          onChange={formik.handleChange}
          value={formik.values.invoice_num}
        />
        <FormText>z.B.: 2020-12345</FormText>
      </FormGroup>
      <FormGroup>
        <Label for="booking_code">Buchungsnummer</Label>
        {formik.errors.booking_code && formik.touched.booking_code
          && <FormText color="danger">{formik.errors.booking_code}</FormText> }
        <Input
          type="text"
          id="booking_code"
          name="booking_code"
          disabled={loading}
          onChange={formik.handleChange}
          value={formik.values.booking_code}
        />
        <FormText>z.B.: 123456789</FormText>
      </FormGroup>
      <div>
        <FormGroup check>
          <Label check>
            <Input
              type="checkbox"
              checked={formik.values.alt_email}
              onChange={() => formik.setFieldValue('alt_email', !formik.values.alt_email)}
            />
            <span className="form-check-sign" />
            <small>{f({id: 'tt.useAlternativeEmailToSendVoucher' })}</small>
          </Label>
        </FormGroup>
        {
          !formik.values.alt_email
          ? <div className="pl-2">
              <FormGroup className="ml-4 pl-4 pt-4">
                <Label for="booking_code">Neue E-Mail Adresse erfassen</Label>
                {formik.errors.email && formik.touched.email
                  && <FormText color="danger">{formik.errors.email}</FormText> }
                <Input
                  type="text"
                  size="small"
                  id="email"
                  name="email"
                  disabled={loading}
                  onChange={formik.handleChange}
                  value={formik.values.email}
                />
              </FormGroup>
            </div>
          : null
        }
      </div>
      { error
        ? <div className="res-error alert alert-warning m-2">{formattedError[error]}</div>
        : null
      }
      <Button
        type="submit"
        color="primary"
        disabled={loading}
        block className="mt-4"
      >
        {loading
          ? <Spinner color="light" />
          : <span>Jetzt Einlösen</span>
        }
      </Button>
    </Form>
  )
}

const validationSchema = Yup.object().shape({
  booking_code: Yup.string()
    .length(9, 'Die Buchungsnummer scheint nicht richtig zu sein. Überprüfen Sie bitte die Eingabe. Die Buchungsnummer ist 9-stellig')
    .required('Sie müssen eine Buchungsnummer angeben'),
  invoice_num: Yup.string()
      .matches(/^20[0-9]{2}-[0-9]{5}$/, 'Die Rechnungsnummer scheint nicht richtig zu sein. Überprüfen Sie bitte die Eingabe.')
      .required('Sie müssen eine Rechnungsnummer angeben'),
  email: Yup.string()
      .email('Die E-Mail Adresse scheint ungültig zu sein')
  // agreed: Yup.boolean()
  //   .oneOf([true], 'Stimmen Sie bitte den Umtauschbedingungen zu'),
})

const convertUrl = `${process.env.API_ENDPOINT}/vouchers/convert_tickets`

const formattedError = {
  'order already converted': 'Diese Tickets wurden bereits eingelöst',
  'order is unpaid': 'Diese Tickets wurden noch nicht bezahlt',
  'order not convertable': 'Die Kombination Rechnungs-/Buchungsnummer wurde nicht erkannt',
  'date allows no conversion': 'Für dieses Datum ist kein Einlösen von Tickets vorgesehen',
  'order not found': 'Die Buchung konnte nicht erkannt werden. Kontrollieren Sie Rechnungs- und Auftragsnummer.',
}

export default function ConvertTicket2Voucher () {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [voucherInfo, setVoucherInfo] = useState({})
  const org = useSelector(makeSelectCurOrg())

  // api POST submit
  const onSubmit = async values => {
    setLoading(true)
    setError(null)
    try {
      const res = await fetch(convertUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(values)
      })
      const jsonRes = await res.json()
      if (jsonRes.error) {
        setError(jsonRes.error)
        setLoading(false)
        return
      }
      if (jsonRes.code) {
        setVoucherInfo({ code: jsonRes.code, value: jsonRes.voucher.value })
      }
    } catch(e) {
      setLoading(false)
      console.error(e)
    }
  }

  const formik = useFormik({
    initialValues: {
      invoice_num: '',
      booking_code: '',
      alt_email: true,
      email: '',
      org_id: org ? org.id : null,
      // agreed: false,
    },
    validationSchema,
    onSubmit,
  })

  if (voucherInfo && voucherInfo.code && voucherInfo.code.length > 1)
    return <ConversionSuccessful voucherInfo={voucherInfo} text={org.portal.tickets_converted_to_voucher_text} />

  return (
    <div className="row">
      <div className="col-lg pr-4">
        <ConversionForm loading={loading} error={error} formik={formik} />
      </div>
      <div className="col-lg col-w-border-left">
        <div className="example-inv-desc">
          Wo finde ich diese Daten?
        </div>
        <div className="example-inv" />
      </div>
    </div>
  )
}